import React from 'react';
import { Link } from '@/components/LocalizedLink';
import { ArrowRight, ChevronRight, Camera } from 'lucide-react';
import { motion } from 'framer-motion';
import { fadeUp, revealOnce } from '@/lib/motion';

function ProductTeaserCard({ product }) {
  return (
    <Link
      to={`/products/${product.id}`}
      className="group block bg-white rounded-lg overflow-hidden shadow-card-lift hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1 border border-saffron-100/50"
      data-testid={`product-card-${product.id}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        {product.image ? (
          <img src={product.image} alt={product.name} width={800} height={600} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-saffron-50/60 to-cream flex items-center justify-center">
            <Camera size={28} className="text-earth/15" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
        <div className="absolute bottom-3 left-3 right-3">
          <h3 className="font-display text-lg font-bold text-white drop-shadow-lg">{product.name}</h3>
        </div>
      </div>
      <div className="p-4">
        <p className="text-xs text-earth/50 font-sans">{product.tagline}</p>
        <div className="mt-3 flex items-center gap-1 text-saffron font-sans text-xs font-semibold group-hover:gap-2 transition-all">
          View Details <ChevronRight size={12} />
        </div>
      </div>
    </Link>
  );
}

export default function ProductsTeaser({ products }) {
  return (
    <section className="py-24 px-4 sm:px-6 lg:px-8" data-testid="products-teaser">
      <div className="max-w-7xl mx-auto">
        <motion.div {...revealOnce} variants={fadeUp} transition={{ duration: 0.6 }} className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-xs uppercase tracking-[0.3em] text-saffron font-sans font-semibold">Our Premium Range</span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-earth-dark mt-3 tracking-tight">
            Export-Grade Spice Powders
          </h2>
          <p className="mt-4 text-earth/60 font-sans leading-relaxed">
            Finely ground from farm-sourced spices and processed to meet international quality standards.
          </p>
        </motion.div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {products.map((product, i) => (
            <motion.div key={product.id} {...revealOnce} variants={fadeUp} transition={{ delay: i * 0.1, duration: 0.5 }}>
              <ProductTeaserCard product={product} />
            </motion.div>
          ))}
        </div>
        <div className="text-center mt-12">
          <Link to="/products" className="inline-flex items-center gap-2 bg-earth-dark text-cream-warm font-sans font-semibold text-sm px-8 py-3.5 rounded-md hover:bg-earth transition-colors" data-testid="view-all-products">
            View Full Catalogue <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}
